/**
 * @file: usePreviewOrderFacadeInfo.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { OrderExtra } from '@euler/app/flows/order/types';
import { Overlay } from '@euler/app/flows/report/components/VehicleFacadeBirdView.shared';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import {
  DefectiveLevel,
  FacadeSiteConfigurationMapByName,
  FacadeSiteConfigurations,
  MediaObject,
} from '@euler/model';
import { AbnormalLevel, SiteInspectionType } from '@euler/model/enum';
import { useMemo } from 'react';

export type FacadeInfoResultGroup = {
  name: string;
  code: string;
  result: string;
  defectiveLevel: DefectiveLevel;
  medias: MediaObject[];
};

export function usePreviewOrderFacadeInfo(detail: OrderExtra) {
  return useMemo(() => {
    const siteInspections =
      detail.preInspection?.filter(
        x => x.inspectionType === SiteInspectionType.Facade,
      ) ?? [];

    const groups: FacadeInfoResultGroup[] = [];
    const groupMap = new Map<string, FacadeInfoResultGroup>();

    for (const siteInspection of siteInspections) {
      const { siteName: name, siteCode: code } = siteInspection;
      for (const item of siteInspection.items) {
        const defectiveLevel = getDefectiveLevel(
          item.abnormalLevel,
          item.severityLevel,
        );
        const result =
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (item.type === 'item' ? item.optionLabel : undefined) ||
          (item.abnormalLevel === AbnormalLevel.Fine ? '记录' : '异常');
        let group = groupMap.get(code);
        if (!group) {
          group = {
            name,
            code,
            result,
            medias: [],
            defectiveLevel,
          };
          groups.push(group);
          groupMap.set(code, group);
        }
        if (group.defectiveLevel < defectiveLevel) {
          group.defectiveLevel = defectiveLevel;
          group.result = result;
        }
        if (item.medias.length) {
          for (const media of item.medias) {
            group.medias.push({
              type: media.type.startsWith('image/') ? 'image' : 'video',
              id: media._key,
              url:
                media._source.type === 'remote'
                  ? media._source.url
                  : media._source.state.status === 'uploaded'
                  ? media._source.state.result.url
                  : media._source.localFileUri,
              coverUrl: media.coverUrl,
              title: media.title,
              subTitle: media.subTitle,
            });
          }
        }
      }
    }

    const groupsWithIssues = groups.filter(
      x => x.defectiveLevel !== DefectiveLevel.Fine,
    );

    const recordGroups = groups.filter(
      x =>
        x.defectiveLevel === DefectiveLevel.Fine &&
        !FacadeSiteConfigurations.find(c => c.code === x.code),
    );

    const normalSites = groups
      .filter(x => x.defectiveLevel === DefectiveLevel.Fine)
      .map(x => FacadeSiteConfigurations.find(c => c.code === x.code)!)
      .filter(x => x);

    const overlays: Overlay[] = [];
    const map = FacadeSiteConfigurationMapByName;
    for (const group of groupsWithIssues) {
      if (!map.has(group.name)) continue;
      const configuration = map.get(group.name)!;
      const color =
        group.defectiveLevel === DefectiveLevel.Urgent ? '#e0463d' : '#ff8a00';
      overlays.push({
        name: configuration.name,
        imagePath: configuration.overlay,
        coords: configuration.coords,
        color,
      });
    }

    return {
      groups,
      groupsWithIssues,
      recordGroups,
      normalSites,
      overlays,
    };
  }, [detail?.preInspection]);
}
